import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Clock, LogIn, LogOut, KeyRound, Download, Activity, Users, Loader2,
  CircleDot, ShieldCheck, FileClock, FileText,
} from "lucide-react";

const PERIODS = [["day", "Jodi a"], ["week", "Semèn"], ["month", "Mwa"]];

export function AttendanceTab() {
  const [me, setMe] = useState(null);
  const [live, setLive] = useState([]);
  const [report, setReport] = useState(null);
  const [audit, setAudit] = useState([]);
  const [period, setPeriod] = useState("week");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(null);
  const [pinDialog, setPinDialog] = useState(null); // {user_id, name}
  const [newPin, setNewPin] = useState("");
  const hbRef = useRef(null);

  const loadMe = useCallback(async () => {
    try { const { data } = await api.get("/attendance/me"); setMe(data); } catch { /* ignore */ }
  }, []);
  const loadLive = useCallback(async () => {
    try { const { data } = await api.get("/admin/attendance/live"); setLive(data); } catch { /* ignore */ }
  }, []);
  const loadReport = useCallback(async (p) => {
    try { const { data } = await api.get("/admin/attendance/report", { params: { period: p } }); setReport(data); } catch { /* ignore */ }
  }, []);
  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get("/admin/attendance/audit"); setAudit(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadMe(); loadLive(); loadAudit(); }, [loadMe, loadLive, loadAudit]);
  useEffect(() => { loadReport(period); }, [period, loadReport]);

  // Live monitoring auto-refresh every 20s
  useEffect(() => {
    const t = setInterval(loadLive, 20000);
    return () => clearInterval(t);
  }, [loadLive]);

  // Heartbeat while checked in
  useEffect(() => {
    if (me?.checked_in && me?.session?.status !== "offline") {
      const beat = () => api.post("/attendance/heartbeat").catch(() => {});
      beat();
      hbRef.current = setInterval(beat, 60000);
      return () => clearInterval(hbRef.current);
    }
  }, [me?.checked_in, me?.session?.status]);

  const checkIn = async () => {
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN dwe gen 4 chif"); return; }
    setBusy("checkin");
    try {
      await api.post("/attendance/checkin", { pin });
      toast.success("Prezans anrejistre — bon travay!");
      setPin(""); await loadMe(); await loadLive(); await loadAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const checkOut = async () => {
    setBusy("checkout");
    try {
      await api.post("/attendance/checkout", { reason: "logout" });
      toast.success("Ou fè depa — jounen an anrejistre.");
      await loadMe(); await loadLive(); await loadAudit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(newPin)) { toast.error("PIN dwe gen 4 chif"); return; }
    setBusy("setpin");
    try {
      await api.put(`/admin/attendance/pin/${pinDialog.user_id}`, { pin: newPin });
      toast.success(`PIN mete pou ${pinDialog.name}`);
      setPinDialog(null); setNewPin(""); await loadLive(); await loadMe();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const exportCsv = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/admin/attendance/export", { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `attendance_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erè ekspòtasyon"); }
    finally { setBusy(null); }
  };

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      const { data } = await api.get("/admin/attendance/export-pdf", { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `attendance_${period}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erè ekspòtasyon PDF"); }
    finally { setBusy(null); }
  };

  const s = me?.session;

  return (
    <div className="space-y-6" data-testid="attendance-tab">
      {/* Self check-in */}
      <div className="border border-[#1C8CFF]/40 bg-[#0F0F0F] p-5 max-w-2xl" data-testid="attendance-self-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-[#1C8CFF] flex items-center justify-center rounded-sm"><Clock className="h-5 w-5 text-white" /></div>
          <div>
            <div className="font-heading font-semibold">Prezans Ou — {me?.title || "Staff"}</div>
            <div className="font-mono-term text-xs text-[#888]">Antre PIN 4 chif ou chak jou pou kòmanse konte lè travay ou</div>
          </div>
        </div>

        {!me?.has_pin ? (
          <div className="text-sm text-[#f5a623] bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-sm p-3">
            Ou poko gen yon PIN. Mande CEO a pou l mete youn pou ou nan lis Ekip la anba a.
          </div>
        ) : me?.checked_in && s?.status !== "offline" ? (
          <div className="flex flex-wrap items-center gap-4" data-testid="attendance-checked-in">
            <div className="flex items-center gap-2">
              <CircleDot className="h-4 w-4 text-[#00F593] animate-pulse" />
              <span className="text-sm text-[#00F593] font-semibold">Aktif · Sou entènèt</span>
            </div>
            <div className="font-mono-term text-xs text-[#888]">
              Antre a: <span className="text-white">{(s?.login_at || "").slice(11, 16)}</span> · Lè aktif: <span className="text-white">{fmt(s?.active_seconds)}</span>
            </div>
            <Button onClick={checkOut} disabled={busy === "checkout"} data-testid="attendance-checkout-button"
              className="h-10 rounded-sm bg-[#FF3B30] text-white hover:bg-[#e02d23] font-semibold ml-auto">
              {busy === "checkout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1.5" /> Fè Depa</>}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3" data-testid="attendance-checkin-form">
            <div>
              <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">PIN 4 chif</label>
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric" maxLength={4} data-testid="attendance-pin-input"
                onKeyDown={(e) => e.key === "Enter" && checkIn()}
                placeholder="••••" className="mt-2 w-32 bg-[#0A0A0A] border-[#222] rounded-sm h-11 text-center tracking-[0.5em] text-lg" />
            </div>
            <Button onClick={checkIn} disabled={busy === "checkin"} data-testid="attendance-checkin-button"
              className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold px-6">
              {busy === "checkin" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1.5" /> Antre Prezans</>}
            </Button>
            {s?.status === "offline" && <span className="text-xs text-[#888]">Ou te fè depa jodi a — antre PIN ankò pou re-kòmanse.</span>}
          </div>
        )}
      </div>

      {/* Live monitoring */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#888] flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Siveyans an Dirèk</span>
          <span className="font-mono-term text-[10px] text-[#555]">{live.filter((x) => x.online).length} sou entènèt · {live.length} staff</span>
        </div>
        <div className="border border-[#222] bg-[#0F0F0F]" data-testid="attendance-live">
          {live.length === 0 ? <div className="p-8 text-center text-[#888] text-sm">Pa gen staff.</div> :
            live.map((x) => (
              <div key={x.user_id} data-testid={`live-staff-${x.user_id}`} className="flex items-center justify-between px-4 py-3 border-b border-[#161616] last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <CircleDot className={`h-4 w-4 shrink-0 ${x.online ? "text-[#00F593] animate-pulse" : "text-[#444]"}`} />
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm truncate">{x.name || "—"} <span className="text-[10px] font-mono-term uppercase text-[#1C8CFF] ml-1">{x.title}</span></div>
                    <div className="font-mono-term text-[11px] text-[#666] truncate">
                      {x.online ? "Ap travay kounye a" : x.checked_in ? "Fè depa" : "Poko antre"}
                      {x.last_seen ? ` · dènye: ${(x.last_seen || "").slice(11, 16)}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <div className="font-heading text-sm font-bold">{x.active_label}</div>
                    <div className="font-mono-term text-[9px] uppercase text-[#555]">lè aktif jodi a</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setPinDialog({ user_id: x.user_id, name: x.name }); setNewPin(""); }}
                    data-testid={`set-pin-${x.user_id}`} className="h-8 rounded-sm text-[#8fb4e6] hover:bg-[#8fb4e6]/10 text-xs">
                    <KeyRound className="h-3.5 w-3.5 mr-1" />{x.has_pin ? "Chanje PIN" : "Mete PIN"}
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Reports */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#888] flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Rapò Lè Travay</span>
          <div className="flex items-center gap-2">
            <div className="flex bg-[#0A0A0A] border border-[#222] rounded-sm overflow-hidden">
              {PERIODS.map(([v, l]) => (
                <button key={v} onClick={() => setPeriod(v)} data-testid={`report-period-${v}`}
                  className={`px-3 h-8 text-xs font-semibold transition-colors ${period === v ? "bg-white text-black" : "text-[#888] hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <Button size="sm" onClick={exportCsv} disabled={busy === "export"} data-testid="attendance-export-button"
              className="h-8 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#0f7ae6] text-xs">
              {busy === "export" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1" /> CSV</>}
            </Button>
            <Button size="sm" onClick={exportPdf} disabled={busy === "pdf"} data-testid="attendance-export-pdf-button"
              className="h-8 rounded-sm bg-[#FF3B30] text-white hover:bg-[#e02d23] text-xs">
              {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5 mr-1" /> PDF</>}
            </Button>
          </div>
        </div>
        {report && (
          <div className="grid grid-cols-3 gap-2.5 mb-3">
            <Mini label="Staff" value={report.totals?.staff ?? 0} />
            <Mini label="Total Èdtan" value={`${report.totals?.hours ?? 0}h`} />
            <Mini label="Sesyon" value={report.totals?.sessions ?? 0} />
          </div>
        )}
        <div className="border border-[#222] bg-[#0F0F0F]" data-testid="attendance-report">
          {(!report || report.ranking.length === 0) ? <div className="p-8 text-center text-[#888] text-sm">Pa gen done pou peryòd sa a.</div> :
            report.ranking.map((r, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#161616] last:border-0">
                <div className="flex items-center gap-3">
                  <span className="font-mono-term text-xs text-[#555] w-5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-heading font-semibold text-sm">{r.name} <span className="text-[10px] font-mono-term uppercase text-[#1C8CFF] ml-1">{r.title}</span></div>
                    <div className="font-mono-term text-[11px] text-[#666]">{r.sessions} sesyon{r.late ? ` · ${r.late} an reta` : ""}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-sm font-bold text-[#00F593]">{r.active_label}</div>
                  <div className="font-mono-term text-[9px] uppercase text-[#555]">{r.hours}h total</div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Audit log */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#888] flex items-center gap-1.5"><FileClock className="h-3.5 w-3.5" /> Jounal Aksyon</span>
          <span className="font-mono-term text-[10px] text-[#555]">efase otomatikman apre chak mwa</span>
        </div>
        <div className="border border-[#222] bg-[#0F0F0F] max-h-72 overflow-y-auto" data-testid="attendance-audit">
          {audit.length === 0 ? <div className="p-8 text-center text-[#888] text-sm">Pa gen aksyon anrejistre.</div> :
            audit.map((a, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-[#161616] last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#555] shrink-0" />
                  <span className="text-sm truncate">{a.name || "—"}</span>
                  <span className={`font-mono-term text-[10px] uppercase px-1.5 py-0.5 rounded-sm ${a.action.includes("failed") ? "bg-[#FF3B30]/10 text-[#FF3B30]" : a.action === "checkin" ? "bg-[#00F593]/10 text-[#00F593]" : "bg-[#1C8CFF]/10 text-[#8fb4e6]"}`}>{a.action}</span>
                </div>
                <span className="font-mono-term text-[11px] text-[#555] shrink-0">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Set PIN dialog */}
      <Dialog open={!!pinDialog} onOpenChange={(o) => { if (!o) setPinDialog(null); }}>
        <DialogContent className="bg-[#0F0F0F] border-[#222] text-white max-w-xs" data-testid="set-pin-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#1C8CFF]" />PIN pou {pinDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#888]">Mete yon PIN 4 chif. Staff la ap sèvi avè l chak jou pou antre prezans li.</p>
            <Input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric" maxLength={4} data-testid="new-pin-input"
              placeholder="••••" className="bg-[#0A0A0A] border-[#222] rounded-sm h-12 text-center tracking-[0.5em] text-xl" />
            <Button onClick={savePin} disabled={busy === "setpin"} data-testid="save-pin-button"
              className="w-full h-11 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#0f7ae6] font-semibold">
              {busy === "setpin" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anrejistre PIN"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#222] p-3 rounded-sm">
      <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#888] mb-1">{label}</div>
      <div className="font-heading text-lg font-bold">{value}</div>
    </div>
  );
}

function fmt(seconds) {
  seconds = Number(seconds || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h${String(m).padStart(2, "0")}m`;
}
